<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import {
  ArrowLeft,
  ChevronRight,
  LoaderCircle,
  Search,
  X,
} from "lucide-vue-next";
import { getCategoryIconPath } from "~/utils/category-icons";
import CategoryIcon from "@/assets/icons/search-category.svg?component";
import { toTitleCase } from "~/utils/search-catalog";
import type {
  SearchCategoryOption,
  SearchServiceOption,
} from "~/utils/search-catalog";

const props = defineProps<{
  category?: string;
  service?: string;
  categories: SearchCategoryOption[];
  placeholder: string;
  inputClass?: string;
}>();

const emit = defineEmits<{
  "update:category": [value: string];
  "update:service": [value: string];
  "selection-complete": [];
}>();

const { t } = useI18n();
const pickerRef = useTemplateRef<HTMLElement>("pickerRef");
const desktopPanelRef = useTemplateRef<HTMLElement>("desktopPanelRef");
const mobilePanelRef = useTemplateRef<HTMLElement>("mobilePanelRef");
const desktopSearchRef = useTemplateRef<HTMLInputElement>("desktopSearchRef");
const mobileSearchRef = useTemplateRef<HTMLInputElement>("mobileSearchRef");

const isOpen = ref(false);
const isMobile = useMediaQuery("(max-width: 767px)");
const level = ref<"categories" | "services">("categories");
const activeCategory = ref<SearchCategoryOption | null>(null);
const query = ref("");
const loadingCategory = ref("");
const loadErrorCategory = ref("");
const explicitAllCategoriesSelection = ref(false);
let previousBodyOverflow = "";

const sharedServices = useState<SearchServiceOption[]>(
  "workers-search-services",
  () => [],
);
const loadedServiceCategoryKeys = useState<string[]>(
  "workers-search-loaded-service-category-keys",
  () => [],
);

function sortCatalog<T extends { sortOrder?: number | null; name: string }>(
  items: T[],
) {
  return [...items].sort(
    (a, b) =>
      Number(a.sortOrder == null) - Number(b.sortOrder == null) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

function categoryLabel(value: string) {
  const key = `category.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

function serviceLabel(value: string) {
  const key = `services.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

const sortedCategories = computed(() => sortCatalog(props.categories));
const selectedCategory = computed(() =>
  props.categories.find(item => item.name === props.category),
);
const selectedService = computed(() => {
  if (!props.category || !props.service) return undefined;

  return sharedServices.value.find(
    item => item.categoryName === props.category && item.name === props.service,
  );
});
const selectedText = computed(() => {
  if (props.service) {
    return selectedService.value
      ? serviceLabel(selectedService.value.name)
      : serviceLabel(props.service);
  }

  if (selectedCategory.value) return categoryLabel(selectedCategory.value.name);
  if (explicitAllCategoriesSelection.value) {
    return t("search.filters.category.all");
  }

  return "";
});

const fieldIsActive = computed(
  () => Boolean(selectedText.value) || isOpen.value,
);

const currentServices = computed(() => {
  if (!activeCategory.value) return [];

  return sortCatalog(
    sharedServices.value.filter(
      item => item.categoryName === activeCategory.value?.name,
    ),
  );
});

const filteredCategories = computed(() => {
  const normalized = query.value.trim().toLowerCase();

  if (!normalized) return sortedCategories.value;

  return sortedCategories.value.filter(item =>
    categoryLabel(item.name).toLowerCase().includes(normalized),
  );
});

const filteredServices = computed(() => {
  const normalized = query.value.trim().toLowerCase();

  if (!normalized) return currentServices.value;

  return currentServices.value.filter(item =>
    serviceLabel(item.name).toLowerCase().includes(normalized),
  );
});

const isLoadingServices = computed(
  () =>
    Boolean(activeCategory.value) &&
    loadingCategory.value === activeCategory.value?.name,
);
const hasLoadError = computed(
  () =>
    Boolean(activeCategory.value) &&
    loadErrorCategory.value === activeCategory.value?.name,
);

const isWholeCategorySelected = computed(() => {
  if (!activeCategory.value) return false;

  return props.category === activeCategory.value.name && !props.service;
});

const isAllCategoriesSelected = computed(
  () =>
    explicitAllCategoriesSelection.value && !props.category && !props.service,
);

function lockBodyScroll() {
  if (typeof document === "undefined" || !isMobile.value) return;

  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;

  document.body.style.overflow = previousBodyOverflow;
}

function openPicker() {
  isOpen.value = true;
  query.value = "";
  level.value = "categories";
  activeCategory.value = null;
}

function closePicker() {
  isOpen.value = false;
  query.value = "";
  level.value = "categories";
  activeCategory.value = null;
}

function clearSelection() {
  explicitAllCategoriesSelection.value = false;
  emit("update:category", "");
  emit("update:service", "");
  closePicker();
}

function completeSelection(
  category = "",
  service = "",
  explicitAllCategories = false,
) {
  explicitAllCategoriesSelection.value = explicitAllCategories;
  emit("update:category", category);
  emit("update:service", service);
  closePicker();
  emit("selection-complete");
}

function selectAllCategories() {
  completeSelection("", "", true);
}

function mergeServices(nextServices: SearchServiceOption[]) {
  const byId = new Map<number, SearchServiceOption>();

  sharedServices.value.forEach(item => byId.set(item.id, item));
  nextServices.forEach(item => byId.set(item.id, item));
  sharedServices.value = [...byId.values()];
}

async function ensureServicesLoaded(category: SearchCategoryOption) {
  if (loadedServiceCategoryKeys.value.includes(category.name)) return;
  if (loadingCategory.value === category.name) return;

  loadingCategory.value = category.name;
  loadErrorCategory.value = "";

  try {
    const services = await $fetch<SearchServiceOption[]>("/api/services", {
      query: { categoryName: category.name },
    });

    mergeServices(services);
    loadedServiceCategoryKeys.value = [
      ...new Set([...loadedServiceCategoryKeys.value, category.name]),
    ];
  } catch {
    loadErrorCategory.value = category.name;
  } finally {
    if (loadingCategory.value === category.name) {
      loadingCategory.value = "";
    }
  }
}

async function openCategory(category: SearchCategoryOption) {
  activeCategory.value = category;
  level.value = "services";
  query.value = "";
  await ensureServicesLoaded(category);
}

function backToCategories() {
  level.value = "categories";
  activeCategory.value = null;
  query.value = "";
}

function selectWholeCategory() {
  if (!activeCategory.value) return;

  completeSelection(activeCategory.value.name);
}

function selectService(service: SearchServiceOption) {
  completeSelection(service.categoryName, service.name);
}

function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") closePicker();
}

function handlePointerDown(event: PointerEvent) {
  if (!isOpen.value) return;

  const target = event.target as Node;

  if (pickerRef.value?.contains(target)) return;
  if (desktopPanelRef.value?.contains(target)) return;
  if (mobilePanelRef.value?.contains(target)) return;

  closePicker();
}

watch(
  () => [props.category, props.service] as const,
  ([category, service]) => {
    if (category || service) {
      explicitAllCategoriesSelection.value = false;
    }
  },
);

watch(
  isOpen,
  value => {
    if (!value) {
      unlockBodyScroll();
      return;
    }

    lockBodyScroll();
    const input = isMobile.value
      ? mobileSearchRef.value
      : desktopSearchRef.value;
    input?.focus();
  },
  { flush: "post" },
);

onMounted(() => {
  window.addEventListener("keyup", handleEscPress);
  document.addEventListener("pointerdown", handlePointerDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keyup", handleEscPress);
  document.removeEventListener("pointerdown", handlePointerDown);
  unlockBodyScroll();
});
</script>

<template>
  <div ref="pickerRef" class="relative min-w-0">
    <div
      role="button"
      tabindex="0"
      class="group flex w-full min-w-0 cursor-pointer items-center gap-2.5 text-left transition duration-300"
      :class="[
        inputClass,
        fieldIsActive ? '!bg-[#E7F3EF] hover:!bg-[#E7F3EF]' : '',
      ]"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
    >
      <slot name="icon" :active="fieldIsActive">
        <Search class="size-[19px] shrink-0" />
      </slot>

      <span
        class="min-w-0 flex-1 truncate text-base font-medium leading-[1.2]"
        :class="selectedText ? 'text-primary-800' : 'text-[#9CA3AF]'"
        :title="selectedText || placeholder"
      >
        {{ selectedText || placeholder }}
      </span>

      <button
        v-if="selectedText"
        type="button"
        class="flex size-7 shrink-0 items-center justify-center rounded-full border border-[#BDD8D0] bg-white text-primary shadow-sm transition hover:bg-white"
        :aria-label="$t('hero.search.picker.clear')"
        @click.stop="clearSelection"
      >
        <X class="size-4" />
      </button>
    </div>

    <Transition name="hero-catalog-fade">
      <div
        v-if="isOpen"
        ref="desktopPanelRef"
        class="absolute left-0 top-[calc(100%+8px)] z-[1200] hidden w-[410px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border border-app-border bg-white shadow-[0_20px_55px_rgba(12,25,24,0.18)] md:block"
        data-scroll-lock-ignore
      >
        <div class="flex items-center gap-2 border-b border-app-border p-3">
          <button
            v-if="level === 'services'"
            type="button"
            class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#F5F7F7] text-app-muted transition hover:bg-primary-50 hover:text-primary"
            :aria-label="$t('hero.search.picker.back')"
            @click="backToCategories"
          >
            <ArrowLeft class="size-[18px]" />
          </button>

          <label
            class="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl bg-[#F5F7F7] px-3 transition focus-within:bg-white focus-within:ring-1 focus-within:ring-primary/30"
          >
            <Search class="size-[18px] shrink-0 text-[#737B86]" />
            <input
              ref="desktopSearchRef"
              v-model="query"
              type="text"
              class="min-w-0 flex-1 bg-transparent text-sm font-medium text-app-text outline-none placeholder:text-[#9CA3AF]"
              :placeholder="
                level === 'categories'
                  ? $t('hero.search.picker.search_categories')
                  : $t('hero.search.picker.search_services')
              "
            />
            <button
              v-if="query"
              type="button"
              class="flex size-6 items-center justify-center rounded-full text-app-muted hover:bg-gray-200 hover:text-app-text"
              @click="query = ''"
            >
              <X class="size-3.5" />
            </button>
          </label>
        </div>

        <div v-if="level === 'categories'">
          <div
            class="app-combobox-scroll overflow-y-auto overscroll-contain"
            :class="
              filteredCategories.length || !query.trim()
                ? 'max-h-[360px]'
                : 'min-h-[120px]'
            "
          >
            <button
              v-if="!query.trim()"
              type="button"
              class="group flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-4 py-3 text-left transition duration-200 hover:bg-primary-50/70"
              :class="isAllCategoriesSelected ? 'bg-primary-50/70' : 'bg-white'"
              @click="selectAllCategories"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#F3F6F5] transition group-hover:bg-white"
              >
                <CategoryIcon class="size-5 text-primary" />
              </span>
              <span
                class="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-app-text"
                :title="$t('search.filters.category.all')"
              >
                {{ $t("search.filters.category.all") }}
              </span>
              <span
                class="flex size-5 shrink-0 items-center justify-center rounded-full border transition"
                :class="
                  isAllCategoriesSelected
                    ? 'border-primary bg-primary'
                    : 'border-[#98A2A8] bg-white group-hover:border-primary'
                "
                aria-hidden="true"
              >
                <span
                  v-if="isAllCategoriesSelected"
                  class="size-1.5 rounded-full bg-white"
                />
              </span>
            </button>

            <button
              v-for="categoryItem in filteredCategories"
              :key="categoryItem.name"
              type="button"
              class="group flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-4 py-3 text-left transition duration-200 last:border-b-0 hover:bg-primary-50/70"
              @click="openCategory(categoryItem)"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#F3F6F5] transition group-hover:bg-white"
              >
                <img
                  :src="getCategoryIconPath(categoryItem.name)"
                  alt=""
                  class="size-5 object-contain"
                  aria-hidden="true"
                />
              </span>
              <span
                class="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-app-text"
                :title="categoryLabel(categoryItem.name)"
              >
                {{ categoryLabel(categoryItem.name) }}
              </span>
              <ChevronRight
                class="size-4 shrink-0 text-[#9AA1A6] transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
              />
            </button>

            <div
              v-if="!filteredCategories.length"
              class="flex min-h-[120px] items-center justify-center px-4 py-8 text-center text-sm text-app-muted"
            >
              {{ $t("hero.search.picker.no_categories") }}
            </div>
          </div>
        </div>

        <div v-else>
          <div
            class="app-combobox-scroll h-[360px] overflow-y-auto overscroll-contain"
          >
            <template v-if="isLoadingServices">
              <div
                v-for="row in 6"
                :key="row"
                class="flex items-center gap-3 px-4 py-3"
              >
                <div class="size-8 animate-pulse rounded-lg bg-gray-100" />
                <div
                  class="h-3.5 animate-pulse rounded-full bg-gray-100"
                  :class="row % 2 ? 'w-[54%]' : 'w-[68%]'"
                />
              </div>
            </template>

            <div v-else-if="hasLoadError" class="px-5 py-8 text-center">
              <p class="text-sm font-medium text-app-text">
                {{ $t("hero.search.picker.load_error") }}
              </p>
              <button
                type="button"
                class="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-primary px-3 text-sm font-semibold text-primary hover:bg-primary-50"
                @click="activeCategory && ensureServicesLoaded(activeCategory)"
              >
                <LoaderCircle class="size-4" />
                {{ $t("hero.search.picker.retry") }}
              </button>
            </div>

            <template v-else>
              <button
                v-if="activeCategory && !query"
                type="button"
                class="group flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-4 py-3 text-left transition hover:bg-primary-50/70"
                :class="
                  isWholeCategorySelected ? 'bg-primary-50/70' : 'bg-white'
                "
                @click="selectWholeCategory"
              >
                <span class="min-w-0 flex-1">
                  <span
                    class="block break-words text-sm font-semibold leading-5 text-app-text"
                  >
                    {{ $t("hero.search.picker.all_in_category") }}
                  </span>
                  <span
                    class="mt-0.5 block break-words text-xs leading-4 text-app-muted"
                  >
                    {{ categoryLabel(activeCategory.name) }}
                  </span>
                </span>
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border transition"
                  :class="
                    isWholeCategorySelected
                      ? 'border-primary bg-primary'
                      : 'border-[#98A2A8] bg-white group-hover:border-primary'
                  "
                  aria-hidden="true"
                >
                  <span
                    v-if="isWholeCategorySelected"
                    class="size-1.5 rounded-full bg-white"
                  />
                </span>
              </button>

              <button
                v-for="serviceItem in filteredServices"
                :key="serviceItem.id"
                type="button"
                class="group flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-4 py-3 text-left transition last:border-b-0 hover:bg-primary-50/70"
                :class="
                  service === serviceItem.name ? 'bg-primary-50/70' : 'bg-white'
                "
                :title="serviceLabel(serviceItem.name)"
                @click="selectService(serviceItem)"
              >
                <span
                  class="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-app-text"
                >
                  {{ serviceLabel(serviceItem.name) }}
                </span>
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border transition"
                  :class="
                    service === serviceItem.name
                      ? 'border-primary bg-primary'
                      : 'border-[#98A2A8] bg-white group-hover:border-primary'
                  "
                  aria-hidden="true"
                >
                  <span
                    v-if="service === serviceItem.name"
                    class="size-1.5 rounded-full bg-white"
                  />
                </span>
              </button>

              <div
                v-if="!filteredServices.length"
                class="flex min-h-[120px] items-center justify-center px-4 py-8 text-center text-sm text-app-muted"
              >
                {{ $t("hero.search.picker.no_services") }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>

    <Teleport to="body">
      <Transition name="hero-catalog-backdrop">
        <button
          v-if="isOpen"
          type="button"
          class="fixed inset-0 z-[1080] bg-black/40 md:hidden"
          :aria-label="$t('hero.search.picker.close')"
          @click="closePicker"
        />
      </Transition>

      <Transition name="hero-catalog-sheet">
        <div
          v-if="isOpen"
          ref="mobilePanelRef"
          class="fixed inset-x-0 bottom-0 z-[1090] flex max-h-[82dvh] min-h-[480px] flex-col overflow-hidden rounded-t-[22px] bg-white p-3 shadow-2xl md:hidden"
          data-scroll-lock-ignore
        >
          <div
            class="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-[#D1D5DB]"
          />

          <div class="mb-3 flex items-center gap-2">
            <button
              v-if="level === 'services'"
              type="button"
              class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#F4F6F6] text-app-text"
              @click="backToCategories"
            >
              <ArrowLeft class="size-[18px]" />
            </button>

            <label
              class="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-app-border bg-white px-3 focus-within:border-primary"
            >
              <Search class="size-[18px] shrink-0 text-[#737B86]" />
              <input
                ref="mobileSearchRef"
                v-model="query"
                type="text"
                class="min-w-0 flex-1 bg-transparent text-sm font-medium text-app-text outline-none placeholder:text-[#9CA3AF]"
                :placeholder="
                  level === 'categories'
                    ? $t('hero.search.picker.search_categories')
                    : $t('hero.search.picker.search_services')
                "
              />
              <button
                v-if="query"
                type="button"
                class="flex size-6 items-center justify-center rounded-full bg-gray-100 text-app-muted"
                @click="query = ''"
              >
                <X class="size-3.5" />
              </button>
            </label>
          </div>

          <div
            v-if="level === 'categories'"
            class="app-combobox-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain"
            :class="
              !filteredCategories.length && query.trim() ? 'min-h-[120px]' : ''
            "
          >
            <button
              v-if="!query.trim()"
              type="button"
              class="flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-3 py-3 text-left active:bg-primary-50/70"
              :class="isAllCategoriesSelected ? 'bg-primary-50/70' : 'bg-white'"
              @click="selectAllCategories"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#F3F6F5]"
              >
                <CategoryIcon class="size-5 text-primary" />
              </span>
              <span
                class="min-w-0 flex-1 break-words text-base font-semibold leading-6 text-app-text"
                :title="$t('search.filters.category.all')"
              >
                {{ $t("search.filters.category.all") }}
              </span>
              <span
                class="flex size-5 shrink-0 items-center justify-center rounded-full border"
                :class="
                  isAllCategoriesSelected
                    ? 'border-primary bg-primary'
                    : 'border-[#98A2A8] bg-white'
                "
                aria-hidden="true"
              >
                <span
                  v-if="isAllCategoriesSelected"
                  class="size-1.5 rounded-full bg-white"
                />
              </span>
            </button>

            <button
              v-for="categoryItem in filteredCategories"
              :key="categoryItem.name"
              type="button"
              class="flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-3 py-3 text-left last:border-b-0 active:bg-primary-50/70"
              @click="openCategory(categoryItem)"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#F3F6F5]"
              >
                <img
                  :src="getCategoryIconPath(categoryItem.name)"
                  alt=""
                  class="size-5 object-contain"
                  aria-hidden="true"
                />
              </span>
              <span
                class="min-w-0 flex-1 break-words text-base font-semibold leading-6 text-app-text"
                :title="categoryLabel(categoryItem.name)"
              >
                {{ categoryLabel(categoryItem.name) }}
              </span>
              <ChevronRight class="size-5 shrink-0 text-[#A0A7AB]" />
            </button>

            <div
              v-if="!filteredCategories.length"
              class="flex min-h-[140px] items-center justify-center px-4 py-10 text-center text-sm text-app-muted"
            >
              {{ $t("hero.search.picker.no_categories") }}
            </div>
          </div>

          <div
            v-else
            class="app-combobox-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            <template v-if="isLoadingServices">
              <div
                v-for="row in 7"
                :key="row"
                class="flex items-center gap-3 px-2 py-3.5"
              >
                <div class="size-9 animate-pulse rounded-lg bg-gray-100" />
                <div
                  class="h-4 animate-pulse rounded-full bg-gray-100"
                  :class="row % 2 ? 'w-[60%]' : 'w-[74%]'"
                />
              </div>
            </template>

            <div v-else-if="hasLoadError" class="px-5 py-10 text-center">
              <p class="text-sm font-medium text-app-text">
                {{ $t("hero.search.picker.load_error") }}
              </p>
              <button
                type="button"
                class="mt-3 inline-flex h-10 items-center gap-2 rounded-lg border border-primary px-4 text-sm font-semibold text-primary"
                @click="activeCategory && ensureServicesLoaded(activeCategory)"
              >
                {{ $t("hero.search.picker.retry") }}
              </button>
            </div>

            <template v-else>
              <button
                v-if="activeCategory && !query"
                type="button"
                class="flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-3 py-3 text-left active:bg-primary-50/70"
                :class="
                  isWholeCategorySelected ? 'bg-primary-50/70' : 'bg-white'
                "
                @click="selectWholeCategory"
              >
                <span class="min-w-0 flex-1">
                  <span
                    class="block break-words text-base font-semibold leading-6 text-app-text"
                  >
                    {{ $t("hero.search.picker.all_in_category") }}
                  </span>
                  <span
                    class="mt-0.5 block break-words text-xs leading-4 text-app-muted"
                  >
                    {{ categoryLabel(activeCategory.name) }}
                  </span>
                </span>
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border"
                  :class="
                    isWholeCategorySelected
                      ? 'border-primary bg-primary'
                      : 'border-[#98A2A8] bg-white'
                  "
                  aria-hidden="true"
                >
                  <span
                    v-if="isWholeCategorySelected"
                    class="size-1.5 rounded-full bg-white"
                  />
                </span>
              </button>

              <button
                v-for="serviceItem in filteredServices"
                :key="serviceItem.id"
                type="button"
                class="flex min-h-[60px] w-full items-center gap-3 border-b border-app-border px-3 py-3 text-left last:border-b-0 active:bg-primary-50/70"
                :class="
                  service === serviceItem.name ? 'bg-primary-50/70' : 'bg-white'
                "
                :title="serviceLabel(serviceItem.name)"
                @click="selectService(serviceItem)"
              >
                <span
                  class="min-w-0 flex-1 break-words text-base font-semibold leading-6 text-app-text"
                >
                  {{ serviceLabel(serviceItem.name) }}
                </span>
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border"
                  :class="
                    service === serviceItem.name
                      ? 'border-primary bg-primary'
                      : 'border-[#98A2A8] bg-white'
                  "
                  aria-hidden="true"
                >
                  <span
                    v-if="service === serviceItem.name"
                    class="size-1.5 rounded-full bg-white"
                  />
                </span>
              </button>

              <div
                v-if="!filteredServices.length"
                class="flex min-h-[140px] items-center justify-center px-4 py-10 text-center text-sm text-app-muted"
              >
                {{ $t("hero.search.picker.no_services") }}
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.hero-catalog-fade-enter-active,
.hero-catalog-fade-leave-active {
  transition:
    opacity 150ms ease,
    transform 150ms ease;
}

.hero-catalog-fade-enter-from,
.hero-catalog-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.985);
}

.hero-catalog-backdrop-enter-active,
.hero-catalog-backdrop-leave-active {
  transition: opacity 170ms ease;
}

.hero-catalog-backdrop-enter-from,
.hero-catalog-backdrop-leave-to {
  opacity: 0;
}

.hero-catalog-sheet-enter-active,
.hero-catalog-sheet-leave-active {
  transition:
    opacity 190ms ease,
    transform 190ms ease;
}

.hero-catalog-sheet-enter-from,
.hero-catalog-sheet-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
